import { STATIC_MAX_PIXELS } from '../constants';
import { calculateOutputDimensions, type Dimensions } from '../lib/dimensions';
import { ConversionError } from '../lib/errors';
import { hasWebpSignature } from '../lib/webp';

export type StaticConversionOptions = Readonly<{
  quality: number;
  longestEdge: number | 'original';
  signal?: AbortSignal;
}>;

export type StaticConversionResult = Readonly<{
  blob: Blob;
  sourceDimensions: Dimensions;
  outputDimensions: Dimensions;
}>;

type DecodedSource = {
  image: CanvasImageSource;
  dimensions: Dimensions;
  close: () => void;
};

function throwIfCancelled(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw new ConversionError('cancelled', 'Conversion cancelled.');
  }
}

async function decodeWithImageElement(file: File, signal?: AbortSignal): Promise<DecodedSource> {
  const url = URL.createObjectURL(file);
  const image = new Image();
  image.decoding = 'async';

  try {
    await new Promise<void>((resolve, reject) => {
      const onAbort = (): void => reject(new ConversionError('cancelled', 'Conversion cancelled.'));
      signal?.addEventListener('abort', onAbort, { once: true });
      image.addEventListener('load', () => resolve(), { once: true });
      image.addEventListener(
        'error',
        () =>
          reject(new ConversionError('decode-failed', 'The browser could not decode this image.')),
        { once: true },
      );
      image.src = url;
    });
    throwIfCancelled(signal);
    if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
      throw new ConversionError('decode-failed', 'The image has invalid dimensions.');
    }
    return {
      image,
      dimensions: { width: image.naturalWidth, height: image.naturalHeight },
      close: () => URL.revokeObjectURL(url),
    };
  } catch (error) {
    URL.revokeObjectURL(url);
    throw error;
  }
}

async function decodeSource(file: File, signal?: AbortSignal): Promise<DecodedSource> {
  throwIfCancelled(signal);
  if ('createImageBitmap' in globalThis) {
    try {
      const bitmap = await createImageBitmap(file, {
        imageOrientation: 'from-image',
        colorSpaceConversion: 'default',
        premultiplyAlpha: 'default',
      });
      throwIfCancelled(signal);
      return {
        image: bitmap,
        dimensions: { width: bitmap.width, height: bitmap.height },
        close: () => bitmap.close(),
      };
    } catch (error) {
      if (signal?.aborted) throw new ConversionError('cancelled', 'Conversion cancelled.');
      if (error instanceof ConversionError) throw error;
      // Safari versions with partial createImageBitmap support use the rendered-orientation image path.
    }
  }
  return decodeWithImageElement(file, signal);
}

function canvasToWebp(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new ConversionError('encode-failed', 'The browser could not encode WebP.'));
        },
        'image/webp',
        quality / 100,
      );
    } catch (error) {
      reject(
        new ConversionError('encode-failed', 'The browser could not encode WebP.', {
          cause: error,
        }),
      );
    }
  });
}

export async function convertStaticImage(
  file: File,
  options: StaticConversionOptions,
): Promise<StaticConversionResult> {
  if (!Number.isInteger(options.quality) || options.quality < 1 || options.quality > 100) {
    throw new RangeError('Quality must be an integer from 1 to 100.');
  }

  const decoded = await decodeSource(file, options.signal);
  try {
    const { width, height } = decoded.dimensions;
    if (width * height > STATIC_MAX_PIXELS) {
      throw new ConversionError('image-too-large', 'Static images are limited to 25 megapixels.');
    }

    const outputDimensions = calculateOutputDimensions(width, height, options.longestEdge);
    throwIfCancelled(options.signal);

    const canvas = document.createElement('canvas');
    try {
      canvas.width = outputDimensions.width;
      canvas.height = outputDimensions.height;
      const context = canvas.getContext('2d', { alpha: true, colorSpace: 'srgb' });
      if (!context) throw new Error('No 2D rendering context');
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.drawImage(decoded.image, 0, 0, outputDimensions.width, outputDimensions.height);
      throwIfCancelled(options.signal);
      const blob = await canvasToWebp(canvas, options.quality);
      throwIfCancelled(options.signal);

      const signature = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
      if (blob.type !== 'image/webp' || !hasWebpSignature(signature)) {
        throw new ConversionError('encode-failed', 'The browser returned an invalid WebP result.');
      }
      return { blob, sourceDimensions: decoded.dimensions, outputDimensions };
    } catch (error) {
      if (error instanceof ConversionError) throw error;
      const code = options.longestEdge === 'original' ? 'canvas-allocation' : 'encode-failed';
      const message =
        code === 'canvas-allocation'
          ? 'The original-size Canvas could not be allocated. Try 2048 or 1280 pixels.'
          : 'The browser could not render this image.';
      throw new ConversionError(code, message, { cause: error });
    } finally {
      canvas.width = 0;
      canvas.height = 0;
    }
  } finally {
    decoded.close();
  }
}
